import React, { useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import * as Yup from "yup";

import SVG from "@/assets/svg";
import CostumeButton from "@/component/button";
import { Visit, VisitStatus } from "@/interfaces/IServiceUser";
import { createVisitAsync, updateVisitAsync } from "@/redux/actions/roster";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setSelectedVisit, updateSingleVisit } from "@/redux/reducers/roster";
import { cancelVisit } from "@/services/roster";
import { LIMITS } from "@/utils/constants";
import VisitLayout from "../dialogueBox/visit-info/visit";
import { CheckBoxInput, RadioInput } from "../input";
import SelectCheckmarks from "../input/multipleSelectInput";
import { errorToast, successToast } from "../toasts";
import { useSocket } from "@/context";

interface ICancelVisit {
  onClose: () => void;
}

export const cancelVisitNoteFieldSx = {
  backgroundColor: "#fff",
  width: "100%",
  maxWidth: "743px",
  boxSizing: "border-box",
  height: "156px",
  borderRadius: "16px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E0E0E0",
  },
} as const;

function CancelVisit({ onClose }: ICancelVisit) {
  const { selectedVisit } = useAppSelector((state) => state.roster);
  const { user } = useAppSelector((state) => state.user);
  const visit = selectedVisit?.extendedProps as Visit;
  const dispatch = useAppDispatch();
  const [selectedStaff, setSelectedStaff] = useState<string[]>(
    visit?.isCancelled
      ? visit?.cancelOptions?.payStaff
      : visit.carerAssigned.map((c) => c._id)
  );
  const socket = useSocket();

  const formik = useFormik({
    initialValues: {
      chargeServiceUser: visit?.isCancelled
        ? visit?.cancelOptions?.chargeServiceUser
        : false,
      shouldPayStaff: visit?.isCancelled
        ? visit?.cancelOptions?.payStaff?.length > 0
        : false,
      note: visit?.isCancelled ? visit?.cancelOptions?.cancellationNote : "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      note: Yup.string()
        .required("Note is required")
        .max(LIMITS.note, `Note should not exceed ${LIMITS.note} characters`),
    }),

    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          payStaff: values.shouldPayStaff ? selectedStaff : [],
          chargeServiceUser: values.chargeServiceUser,
          cancellationNote: values.note,
          cancelledBy: user?._id,
          cancelledAt: new Date(),
        };

        if (!visit?.origin && !visit?.isCancelled) {
          const { _id, ...newVisit } = visit;

          // update the original visit exdate
          // create a new visit based on the original visit
          await Promise.all([
            // Update the old visit. Adding the date to exdate array
            dispatch(
              updateVisitAsync({
                id: visit?._id,
                data: {
                  exdate: [
                    ...new Set([
                      ...(visit?.exdate ? visit.exdate : []),
                      dayjs(selectedVisit?.start).format("YYYY-MM-DD"),
                    ]),
                  ],
                },
              })
            ),
            dispatch(
              createVisitAsync({
                ...newVisit,
                rrule: newVisit?.originalRRule || { frequency: "daily" },
                serviceUser: newVisit?.serviceUser?._id,
                startTime: selectedVisit?.start,
                endTime: selectedVisit?.end,
                startDate: selectedVisit?.start,
                endDate: selectedVisit?.start,
                exdate: [],
                notes: newVisit?.notes?.map((n) => n._id),
                carerAssigned: newVisit?.carerAssigned?.map((c) => c?._id),
                anotherCarers: newVisit?.anotherCarers?.map(
                  (n) => n?.carer?._id
                ),
                isCancelled: true,
                status: VisitStatus.CANCELLED,
                cancelOptions: payload,
              })
            ),
          ]);

          successToast("Visit cancelled successfully");
          dispatch(setSelectedVisit(null));
          onClose();
          return;
        } else if (visit?.isCancelled) {
          // if already cancelled, just update the cancel options
          dispatch(
            updateVisitAsync({
              id: visit?._id,
              data: {
                cancelOptions: payload,
              },
            })
          );
          successToast("Changes saved successfully");
          dispatch(setSelectedVisit(null));
          onClose();
          return;
        }

        // proceed with normal cancellation
        const response = await cancelVisit(visit?._id, payload);
        dispatch(
          updateSingleVisit({
            _id: response?.data?._id,
            isCancelled: response?.data?.isCancelled,
            cancelOptions: response?.data?.cancelOptions,
          })
        );
        socket?.emit("visit:refreshAll"); // Refresh because cancel/uncancel status is updated by the backend.
        successToast("Visit cancelled successfully");
        dispatch(setSelectedVisit(null));
        onClose();
      } catch {
        errorToast("Failed to cancel visit ");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const unCancelVisit = async () => {
    // if already cancelled, just update the cancel options
    await dispatch(
      updateVisitAsync({
        id: visit?._id,
        data: {
          isCancelled: false,
          cancelOptions: undefined,
        },
      })
    );
    // to remove cancelOptions from redux store
    dispatch(
      updateSingleVisit({
        _id: visit?._id,
        isCancelled: false,
        cancelOptions: undefined,
      })
    );
    socket?.emit("visit:refreshAll"); // Refresh because cancel/uncancel status is updated by the backend.
    successToast("Changes saved successfully");
    dispatch(setSelectedVisit(null));
    onClose();
  };

  return (
    <VisitLayout>
      <Box className="Add-medication">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 600, fontSize: "20px" }}>
            {visit?.isCancelled
              ? "Are you sure you want to uncancel/edit this visit?"
              : "Are you sure you want to cancel this visit?"}
          </Typography>

          <Box onClick={onClose} sx={{ cursor: "pointer" }}>
            <SVG.Cross />
          </Box>
        </Box>

        <Box
          sx={{
            marginBottom: "16px",
            flexDirection: "column",
            display: "flex",
          }}
        >
          <RadioInput
            label="Include this visit in customer billing ?"
            labelStyle={{
              color: "#000",
              margin: 0,
              top: "0px",
              fontSize: "14px!important",
              fontWeight: "400!important",
              textTransform: "capitalize",
            }}
            options={[
              { label: "Yes", value: "charge" },
              { label: "No", value: "doNotCharge" },
            ]}
            value={formik.values.chargeServiceUser ? "charge" : "doNotCharge"}
            onChange={(e) =>
              formik.setFieldValue(
                "chargeServiceUser",
                e.target.value === "charge"
              )
            }
          />
          <CheckBoxInput
            checked={formik.values.shouldPayStaff}
            disabled={!visit?.origin}
            onChange={(e) => {
              formik.setFieldValue("shouldPayStaff", e.target.checked);
              if (e.target.checked === false) {
                setSelectedStaff([]);
              }
            }}
            label="Pay staff for this visit."
            sx={{
              border: "none",
            }}
          />
        </Box>

        {formik.values.shouldPayStaff && (
          <Box sx={{ marginBottom: "20px" }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                marginBottom: "8px",
                fontSize: "16px",
                color: "#1F4970",
              }}
            >
              Select carers
            </Typography>
            <SelectCheckmarks
              items={visit.carerAssigned.map((c) => ({
                label: c.name,
                value: c._id,
              }))} // only assigned carers can be selected
              multiple={true}
              classname="userdailylist"
              label=""
              limitTags={-1}
              inputProps={{
                className: "",
              }}
              value={selectedStaff}
              handleChange={(vl) => {
                setSelectedStaff(vl as string[]);
              }}
            />
          </Box>
        )}

        <Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              marginBottom: "8px",
              fontSize: "16px",
              color: "#1F4970",
            }}
          >
            Add note*
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            name="note"
            value={formik.values.note}
            placeholder="Type here"
            onChange={(e) => {
              const value = e.target.value;

              if (value.length <= LIMITS.note) {
                formik.setFieldValue("note", value);
              }

              if (value.length > LIMITS.note) {
                formik.setErrors({
                  ...formik.errors,
                  note: `Note should not exceed ${LIMITS.note} characters`,
                });
              } else {
                formik.setErrors({
                  ...formik.errors,
                  note: undefined,
                });
              }
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.note && Boolean(formik.errors.note)}
            helperText={formik.touched.note && formik.errors.note}
            sx={cancelVisitNoteFieldSx}
            slotProps={{
              htmlInput: {
                maxLength: LIMITS.note,
              },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 3 }}>
          {visit?.isCancelled ? (
            <CostumeButton
              // loading={formik.isSubmitting}
              onClick={() => unCancelVisit()}
              className="invites"
              sx={{
                width: "142px",
                background: "#000 !important",
                "&:hover": {
                  background: "#333333 !important",
                },
              }}
            >
              Uncancel visit
            </CostumeButton>
          ) : (
            <CostumeButton
              loading={formik.isSubmitting}
              onClick={() => formik.handleSubmit()}
              className="invites"
              sx={{
                width: "142px",
                backgroundColor: "#D32F2F",
                "&:hover": {
                  backgroundColor: "#B71C1C",
                },
              }}
            >
              Cancel visit
            </CostumeButton>
          )}

          {visit?.isCancelled && (
            <CostumeButton
              onClick={() => formik.handleSubmit()}
              className="invite"
              disabled={formik.isSubmitting}
              loading={formik.isSubmitting}
              sx={{
                width: "142px",
                marginLeft: "16px",
                "&:hover": {
                  backgroundColor: "#E0E0E0",
                },
              }}
            >
              Save changes
            </CostumeButton>
          )}
        </Box>
      </Box>
    </VisitLayout>
  );
}

export default CancelVisit;
